import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {updateItemsOnArchive, updateItemsOnUnarchive} from 'archive/ArchiveStoreUtil';
import * as EventConstants from 'constants/EventConstants';
import {LegacyAssaySchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {RequestSchema} from 'requests/RequestConstants';
import filterAndSortAssaySchemas from 'results/assay-schemas/filterAndSortAssaySchemas';
import {SampleGroupSchema} from 'workflows/WorkflowConstants';

import {ASSAY_TYPES, AssaySchema, AssayRunSchema, ResultSchema} from './assay-schemas/AssaySchemaConstants';

/**
 * Note: Request schemas in the flux AssayStore should be synced with the redux store in all actions.
 * Relevant reducer for redux store: `client/requests/schemas/reduceRequestSchemas.ts`
 */
class AssayStore extends Store {
  assaySchemasById!: {[id: string]: AssaySchema} | null;

  resetState(): void {
    this.assaySchemasById = null;
  }

  __serverBootstrap({schemas}: {schemas: Array<AssaySchema>}): void {
    this.assaySchemasById = {};
    for (const schema of schemas) {
      this.assaySchemasById[schema.id] = schema;
    }
  }

  getAssaySchemas(schemaType: LegacyAssaySchemaType): Array<AssaySchema> | null {
    if (this.assaySchemasById == null) {
      // Schemas have not been fetched yet
      return null;
    }
    // eslint-disable-next-line no-restricted-syntax
    const assaySchemas = _.values(this.assaySchemasById);
    return filterAndSortAssaySchemas(assaySchemas, schemaType);
  }

  getRequestSchemas(): Array<RequestSchema> | null {
    // TS convenience function
    return this.getAssaySchemas(ASSAY_TYPES.ASSAY_REQUEST) as Array<RequestSchema> | null;
  }

  getResultSchemas(): Array<ResultSchema> | null {
    // TS convenience function
    return this.getAssaySchemas(ASSAY_TYPES.ASSAY_RESULT) as Array<ResultSchema> | null;
  }

  getRunSchemas(): Array<AssayRunSchema> | null {
    // TS convenience function
    return this.getAssaySchemas(ASSAY_TYPES.ASSAY_RUN) as Array<AssayRunSchema> | null;
  }

  getRequestSchema(schemaId: string): RequestSchema | null {
    // TS convenience function
    return this.assaySchemasById && (this.assaySchemasById[schemaId] as RequestSchema | null);
  }

  getSampleGroupSchema(schemaId: string): SampleGroupSchema | null {
    if (!this.assaySchemasById) {
      return null;
    }
    // eslint-disable-next-line no-restricted-syntax
    return _.findWhere(_.pluck(Object.values(this.assaySchemasById), 'sampleGroupSchema'), {id: schemaId});
  }

  getAssaySchema(schemaId: string): AssaySchema | null {
    return this.assaySchemasById && this.assaySchemasById[schemaId];
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.ASSAY_SCHEMAS_LISTED: {
        const {assaySchemas} = action;
        this.assaySchemasById = _.indexBy(assaySchemas, (schema) => schema.id);
        this.emitChange();
        break;
      }
      case EventConstants.ASSAY_SCHEMA_FETCHED: {
        const {assaySchema} = action;
        if (this.assaySchemasById == null) {
          this.assaySchemasById = {};
        }
        this.assaySchemasById = reactUpdate(this.assaySchemasById, {[assaySchema.id]: {$set: assaySchema}});
        this.emitChange();
        break;
      }
      case EventConstants.ASSAY_SCHEMAS_UPDATED_FROM_GQL: {
        const {assaySchemas} = action;
        this.assaySchemasById = {...this.assaySchemasById, ..._.indexBy(assaySchemas, (schema) => schema.id)};
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ARCHIVE_ASSAY_SCHEMAS_COMPLETED: {
        const {itemIds, archiveRecordsByItemId} = action;
        this.assaySchemasById = updateItemsOnArchive(this.assaySchemasById!, itemIds, archiveRecordsByItemId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_UNARCHIVE_ASSAY_SCHEMAS_COMPLETED: {
        const {itemIds} = action;
        this.assaySchemasById = updateItemsOnUnarchive(this.assaySchemasById!, itemIds);
        this.emitChange();
        break;
      }
      default:
        break;
    }
    return true;
  }
}

const assayStore = new AssayStore(AppDispatcher);
export default assayStore;
